// app/src/api/core/axiosBase.ts

import axios from "axios"
import axiosRetry from "axios-retry"
import { RETRY_COUNT } from "@core/config"
import { getCurrentServerUrls } from "@core/utils/serverConfig"
import { recordSessionExit, stringifyLogData, type SessionExitReason } from "@core/utils/sessionExitTracker"
import {
  clearStoredAuthState,
  getNativeWebSessionClient,
  getStoredWebSessionUser,
  getStoredWebCsrfToken,
  isWebSessionAuthMode,
  refreshStoredWebSessionExpiry,
  updateStoredWebCsrfToken,
} from "@core/utils/webSessionAuth"
import { resolveStatusCodeMessage } from "@core/utils/errorUtil"
import { generateIdempotencyKey } from "./idempotency"

/**
 * 기본 axios 인스턴스를 생성하는 함수
 * serverApi와 queueApi에서 공통으로 사용
 *
 * @param timeout 요청 타임아웃 시간 (밀리초)
 * @param retryDelayMultiplier 재시도 지연 시간 배수
 * @param apiName API 이름 (로그용)
 * @returns 설정된 axios 인스턴스
 */

const RETRY_TIME_WAIT_SECOND = 400
const SESSION_EXPIRY_HANDLE_DEBOUNCE_MS = 1000
const SESSION_REVALIDATION_TIMEOUT_MS = 5000
const WEB_SESSION_RETRY_FLAG = "__sigmaWebSessionRetried"

const AUTH_EXCLUDED_PATHS = ["/member/login/", "/web/member/login/", "/web/auth/login/", "/web/auth/csrf/", "/web/auth/session/"]
const SESSION_EXPIRY_EXCLUDED_PATHS = ["/sms/proxy/"]
let lastExpiredSessionHandledAt = 0
let pendingSessionRevalidationPromise: Promise<"valid" | "expired" | "unknown"> | null = null

const getNativeAppVariant = () => {
  if (typeof window === "undefined") {
    return null
  }

  return typeof __APP_VARIANT__ === "string" && __APP_VARIANT__.trim().length > 0 ? __APP_VARIANT__ : null
}

const isAbsoluteUrl = (url?: string): boolean => {
  if (!url) {
    return false
  }

  return /^https?:\/\//i.test(url)
}

const buildWebSessionUrl = (url?: string): string | undefined => {
  if (!url || isAbsoluteUrl(url) || url.startsWith("/web/")) {
    return url
  }

  if (url.startsWith("/")) {
    return `/web${url}`
  }

  return `/web/${url}`
}

const requiresCsrfHeader = (method?: string): boolean => {
  if (!method) {
    return false
  }

  return ["post", "put", "patch", "delete"].includes(method.toLowerCase())
}

const shouldSkipAuthHandling = (url?: string): boolean => {
  if (!url) {
    return false
  }

  if (AUTH_EXCLUDED_PATHS.some(path => url.includes(path))) {
    return true
  }

  if (SESSION_EXPIRY_EXCLUDED_PATHS.some(path => url.includes(path))) {
    return true
  }

  return url.includes("/public/")
}

const clearStoredAuthTokens = () => {
  clearStoredAuthState()
}

const getCurrentRoutePath = () => {
  const hashPath = window.location.hash.replace(/^#/, "")
  if (hashPath) {
    return hashPath.split("?")[0] || "/"
  }

  return window.location.pathname || "/"
}

const getExpiredSessionRedirectPath = () => {
  if (getCurrentRoutePath().startsWith("/android")) {
    return "/android/login"
  }

  return "/login"
}

const handleExpiredSession = (reason: SessionExitReason, details: Record<string, unknown> = {}) => {
  const now = Date.now()

  if (now - lastExpiredSessionHandledAt > SESSION_EXPIRY_HANDLE_DEBOUNCE_MS) {
    lastExpiredSessionHandledAt = now
    recordSessionExit(reason, details)
  }

  clearStoredAuthTokens()
  window.dispatchEvent(new Event("storage"))

  const redirectPath = getExpiredSessionRedirectPath()
  const currentRoutePath = getCurrentRoutePath()

  if (currentRoutePath !== redirectPath) {
    window.location.replace(`#${redirectPath}`)
  }
}

const retryWebSessionRequestWithPolicy = (instance: any, requestConfig: any, apiName: string) => {
  const nextRequestConfig = {
    ...requestConfig,
    [WEB_SESSION_RETRY_FLAG]: true,
  }

  if (apiName === "API" || apiName === "CriticalAPI") {
    return instance.request(nextRequestConfig)
  }

  return null
}

const isStoredWebSessionExpired = () => {
  const sessionUser = getStoredWebSessionUser()
  if (!sessionUser?.exp || typeof sessionUser.exp !== "number") {
    return false
  }

  return sessionUser.exp <= Math.floor(Date.now() / 1000)
}

const revalidateWebSession = async (apiName: string) => {
  if (pendingSessionRevalidationPromise) {
    return pendingSessionRevalidationPromise
  }

  pendingSessionRevalidationPromise = (async () => {
    const { API_BASE_URL } = getCurrentServerUrls()

    if (!API_BASE_URL) {
      return isStoredWebSessionExpired() ? "expired" : "unknown"
    }

    const nativeWebSessionClient = getNativeWebSessionClient()
    const headers: Record<string, string> = {}

    if (nativeWebSessionClient) {
      headers["X-Sigma-Native-Client"] = nativeWebSessionClient

      const nativeAppVariant = getNativeAppVariant()
      if (nativeAppVariant) {
        headers["X-Sigma-App-Variant"] = nativeAppVariant
      }
    }

    const abortController = typeof AbortController !== "undefined" ? new AbortController() : null
    const timeoutId = abortController
      ? window.setTimeout(() => {
          abortController.abort()
        }, SESSION_REVALIDATION_TIMEOUT_MS)
      : null

    try {
      const response = await fetch(`${API_BASE_URL}/web/auth/session/`, {
        method: "GET",
        credentials: "include",
        headers,
        signal: abortController?.signal,
      })

      if (response.ok) {
        try {
          const responseData = await response.json()
          const expiresIn = responseData?.result?.expiresIn || responseData?.expiresIn
          if (typeof expiresIn === "number") {
            refreshStoredWebSessionExpiry(expiresIn)
          }

          const csrfToken = responseData?.result?.csrfToken || responseData?.csrfToken
          if (typeof csrfToken === "string" && csrfToken.trim()) {
            updateStoredWebCsrfToken(csrfToken)
          }
        } catch {
          // 세션 재확인 성공 여부만 필요하므로 JSON 파싱 실패는 무시
        }

        return "valid"
      }

      if (response.status === 401 || response.status === 403) {
        return "expired"
      }

      return isStoredWebSessionExpired() ? "expired" : "unknown"
    } catch (revalidationError) {
      console.warn(
        `[${apiName} 401 revalidate failed] ${stringifyLogData({
          error: revalidationError,
          locallyExpired: isStoredWebSessionExpired(),
        })}`
      )
      return isStoredWebSessionExpired() ? "expired" : "unknown"
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      pendingSessionRevalidationPromise = null
    }
  })()

  return pendingSessionRevalidationPromise
}

const isMeaningfulServerMessage = (message: unknown): message is string => {
  if (typeof message !== "string") {
    return false
  }

  const trimmed = message.trim()
  if (!trimmed) {
    return false
  }

  return trimmed !== "error" && trimmed !== "validation_error" && trimmed !== "internal_server_error" && trimmed !== "server_error"
}

const shouldSuppressExpectedErrorLog = (url: unknown, statusCode: unknown) => {
  const normalizedUrl = String(url || "")
  const normalizedStatusCode = Number(statusCode || 0)

  return (
    (normalizedUrl.includes("/hospital/public/stamp/") && (normalizedStatusCode === 404 || normalizedStatusCode === 403)) ||
    (normalizedUrl.includes("/encounter/encounter_herbal/") &&
      (normalizedUrl.includes("/editable/") || normalizedUrl.includes("/prescriptions/")) &&
      normalizedStatusCode === 404)
  )
}

export const createAxiosInstance = (timeout: number = 15_000, retryDelayMultiplier: number = 1000, apiName: string = "API") => {
  // axios 인스턴스 생성
  const instance = axios.create({
    timeout,
  })

  // 네트워크 에러 재시도 설정
  axiosRetry(instance, {
    retries: RETRY_COUNT, // 최대 10회
    retryDelay: retryCount => Math.min(retryDelayMultiplier * retryCount, RETRY_TIME_WAIT_SECOND), // 최대 400ms로 제한
    retryCondition: error => {
      const method = error.config?.method?.toLowerCase()
      const hasIdempotencyKey = error.config?.headers?.["Idempotency-Key"]

      // DELETE 요청은 여전히 재시도하지 않음 (데이터 손실 위험)
      if (method === "delete") {
        return false
      }

      // POST, PUT, PATCH는 Idempotency-Key가 있는 경우에만 재시도
      if (method && ["post", "put", "patch"].includes(method)) {
        if (hasIdempotencyKey) {
          return axiosRetry.isNetworkOrIdempotentRequestError(error)
        } else {
          return false
        }
      }

      // GET 요청은 항상 재시도
      return axiosRetry.isNetworkOrIdempotentRequestError(error)
    },
    onRetry: (retryCount, error, requestConfig) => {
      // 재시도 중에는 간단한 로그만 출력 (에러 보고 안함)
    },
  })

  // 요청 인터셉터: 매번 localStorage에서 최신 IP:PORT 덮어쓰기 + 인증 헤더 자동 주입
  instance.interceptors.request.use(async (config: any) => {
    const { API_BASE_URL } = getCurrentServerUrls()
    const webSessionMode = isWebSessionAuthMode()
    const nativeWebSessionClient = getNativeWebSessionClient()

    if (!API_BASE_URL) {
      const error = new Error("서버가 설정되지 않았습니다. 잠시 후 다시 시도해주세요.")
      return Promise.reject(error)
    }
    config.baseURL = API_BASE_URL

    if (nativeWebSessionClient && typeof config.url === "string" && config.url.startsWith("/web/")) {
      config.headers = config.headers || {}
      config.headers["X-Sigma-Native-Client"] = config.headers["X-Sigma-Native-Client"] || nativeWebSessionClient

      const nativeAppVariant = getNativeAppVariant()
      if (nativeAppVariant) {
        config.headers["X-Sigma-App-Variant"] = config.headers["X-Sigma-App-Variant"] || nativeAppVariant
      }
    }

    if (webSessionMode) {
      config.url = buildWebSessionUrl(config.url)
      config.withCredentials = true

      if (nativeWebSessionClient) {
        config.headers = config.headers || {}
        config.headers["X-Sigma-Native-Client"] = config.headers["X-Sigma-Native-Client"] || nativeWebSessionClient

        const nativeAppVariant = getNativeAppVariant()
        if (nativeAppVariant) {
          config.headers["X-Sigma-App-Variant"] = config.headers["X-Sigma-App-Variant"] || nativeAppVariant
        }
      }

      if (requiresCsrfHeader(config.method)) {
        const csrfToken = getStoredWebCsrfToken()
        if (csrfToken) {
          config.headers = config.headers || {}
          config.headers["X-CSRFToken"] = config.headers["X-CSRFToken"] || csrfToken
        }
      }
    }

    // 🔑 Idempotency-Key 자동 추가 (POST/PATCH/PUT 요청)
    const method = config.method?.toLowerCase()
    if (method && ["post", "patch", "put"].includes(method)) {
      config.headers = config.headers || {}
      // 기존 Idempotency-Key가 없는 경우에만 추가
      if (!config.headers["Idempotency-Key"]) {
        config.headers["Idempotency-Key"] = generateIdempotencyKey()
      }
    }

    return config
  })

  // 응답/에러 인터셉터
  instance.interceptors.response.use(
    (response: any) => {
      const webSessionMode = isWebSessionAuthMode()
      if (webSessionMode && !shouldSkipAuthHandling(response?.config?.url)) {
        const expiresIn = response?.data?.result?.expiresIn || response?.data?.expiresIn
        if (typeof expiresIn === "number") {
          refreshStoredWebSessionExpiry(expiresIn)
        }
      }

      const csrfToken = response?.data?.result?.csrfToken || response?.data?.csrfToken
      if (typeof csrfToken === "string" && csrfToken.trim()) {
        updateStoredWebCsrfToken(csrfToken)
      }

      return response
    },
    async (error: any) => {
      const statusCode = error.response?.status
      const errorCode = error.response?.data?.code
      const webSessionMode = isWebSessionAuthMode()
      const alreadyRetried = Boolean(error.config?.[WEB_SESSION_RETRY_FLAG])
      const method = error.config?.method?.toLowerCase()
      const hasIdempotencyKey = error.config?.headers?.["Idempotency-Key"]
      const canRetryAfterRevalidation = method === "get" || (["post", "put", "patch"].includes(method) && hasIdempotencyKey)

      if (statusCode === 401) {
        console.warn(
          `[${apiName} 401] ${stringifyLogData({
            url: error.config?.url,
            method: error.config?.method,
            statusCode,
            errorCode,
            data: error.response?.data,
          })}`
        )
      }

      if (statusCode === 401 && webSessionMode && !shouldSkipAuthHandling(error.config?.url)) {
        const revalidationResult = await revalidateWebSession(apiName)

        if (revalidationResult === "expired") {
          handleExpiredSession("token_not_valid_response", {
            apiName,
            triggerUrl: error.config?.url,
            method,
            statusCode,
            errorCode,
            data: error.response?.data,
            revalidationResult,
          })
          return Promise.reject(error)
        }

        if (revalidationResult === "valid" && error.config && !alreadyRetried && canRetryAfterRevalidation) {
          const retriedRequest = retryWebSessionRequestWithPolicy(instance, error.config, apiName)
          if (retriedRequest) {
            return retriedRequest
          }
        }
      }

      let errorData = error.response?.data

      // Django 에러 페이지인지 확인 (HTML 형태이고 Django 관련 내용 포함)
      if (typeof errorData === "string" && errorData.includes("<!DOCTYPE html>") && (errorData.includes("Django") || errorData.includes("URLconf"))) {
        errorData = "[장고 에러페이지]"
      }

      // 서버 메시지 우선: message가 비어있거나 generic일 때만 최소 fallback 적용
      const responseMessage = typeof errorData === "object" && errorData !== null ? errorData.message : undefined

      if (!isMeaningfulServerMessage(responseMessage)) {
        const fallbackMessage = resolveStatusCodeMessage(statusCode) || "요청을 처리할 수 없습니다."
        error.message = fallbackMessage
        if (typeof errorData === "object" && errorData !== null) {
          errorData.message = fallbackMessage
        }
      }

      const suppressExpectedErrorLog = shouldSuppressExpectedErrorLog(error.config?.url, statusCode)
      // 재시도 가능한 요청인지 확인
      const isRetryableRequest =
        (method === "get" || (["post", "put", "patch"].includes(method) && hasIdempotencyKey)) && axiosRetry.isNetworkOrIdempotentRequestError(error)

      if (suppressExpectedErrorLog) {
        return Promise.reject(error)
      }

      // 재시도 가능한 요청인 경우: 재시도가 모두 완료된 후에만 에러 로그 출력
      if (isRetryableRequest) {
        const currentRetryCount = error.config?.["axios-retry"]?.retryCount || 0
        if (currentRetryCount >= RETRY_COUNT) {
          // 모든 재시도가 실패한 경우에만 에러 보고
          console.error(
            `[${apiName} ✕] 재시도 ${RETRY_COUNT}회 모두 실패 → ${error.config?.url} ${stringifyLogData({
              statusCode: error.response?.status,
              errorCode,
              data: errorData,
            })}`
          )
        }
        // 재시도 중인 경우에는 에러 로그를 출력하지 않음
      } else {
        // 재시도하지 않는 요청은 바로 에러 로그 출력
        console.error(
          `[${apiName} ✕] ${error.code || error.message} → ${error.config?.url} ${stringifyLogData({
            statusCode: error.response?.status,
            errorCode,
            data: errorData,
          })}`
        )
      }

      return Promise.reject(error)
    }
  )

  return instance
}
/**
 * serverApi용 axios 인스턴스 생성
 */
export const createServerAxiosInstance = () => {
  return createAxiosInstance(15_000, 1000, "API")
}

/**
 * queueApi용 axios 인스턴스 생성
 */
export const createQueueAxiosInstance = () => {
  const instance = createAxiosInstance(30_000, 1500, "QueueAPI")

  // QueueAPI 전용 성공 로그 추가
  instance.interceptors.response.use(
    (response: any) => {
      return response
    },
    (error: any) => {
      // 에러 로그는 createAxiosInstance에서 이미 처리됨
      return Promise.reject(error)
    }
  )

  return instance
}
